import { isEmpty } from 'lodash';
import type { ComponentType, ReactNode } from 'react';
import { useMemo } from 'react';

import {
  CurrentNetworkProvider,
  CurrentSessionProvider,
  CurrentUrlProvider,
  CurrentUserProvider,
  getCurrentUrl,
} from 'hb-react/frontoffice/contexts/DefaultAppContexts';
import CombineProvider from 'hb-react/shared/components/CombineProvider';
import ErrorBoundary from 'hb-react/shared/components/ErrorBoundary';
import QueryClientProvider from 'hb-react/shared/providers/QueryClient';
import ThemeProvider from 'hb-react/style/ThemeProvider';
import type { Network } from 'hb-react/types/network';
import type { User } from 'hb-react/types/user';

interface CustomProvider {
  provider: ComponentType<any>;
  [key: string]: unknown;
}

interface AppProps {
  children: ReactNode;
  colors?: Network['colors'];
  network?: Network;
  networkId?: Network['id'];
  providers?: CustomProvider[];
  session?: Record<string, unknown>;
  theme: 'po' | 'fo' | 'bo';
  theming?: Network['theming'];
  user?: User;
}

const App = ({
  children,
  colors,
  network,
  networkId,
  providers = [],
  session,
  theme,
  theming,
  user,
}: AppProps) => {
  const allProviders = useMemo(
    () =>
      [
        {
          provider: QueryClientProvider,
        },
        (!isEmpty(network) || networkId) && {
          provider: CurrentNetworkProvider,
          currentNetwork: network,
          currentNetworkId: networkId,
        },
        user && {
          provider: CurrentUserProvider,
          currentUser: user,
        },
        session && {
          provider: CurrentSessionProvider,
          currentSession: session,
        },
        {
          provider: CurrentUrlProvider,
          currentUrl: getCurrentUrl(),
        },
        {
          provider: ThemeProvider,
          theme,
          options: {
            customNetworkColors: colors ?? network?.colors ?? {},
            customNetworkTheming: theming ??
              network?.theming ?? { po: {}, fo: {}, global: {} },
          },
        },
        ...providers,
      ].filter(Boolean),
    [network, networkId, user, session, theme, colors, theming, providers],
  );

  return (
    <CombineProvider providers={allProviders}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </CombineProvider>
  );
};

export default App;
